import { useEffect, useId, useRef, useState } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import connectionsCopy from '@/i18n/ns/connections';
import { Glyph } from './glyphs';

function selectContents(node: HTMLElement | null) {
  const selection = typeof window.getSelection === 'function' ? window.getSelection() : null;
  if (!node || !selection || typeof document.createRange !== 'function') return;
  const range = document.createRange();
  range.selectNodeContents(node);
  selection.removeAllRanges();
  selection.addRange(range);
}

/**
 * A value to paste elsewhere, with its own Copy button. The copied value is
 * the exact string, never the wrapped on-screen text. Where the clipboard is
 * unavailable (insecure origins, denied permission) the text is selected
 * instead so the person can copy it themselves.
 */
export function CopyLine({
  value,
  label,
  kind = 'code',
  caption,
}: {
  value: string;
  /** What is copied, completing the button's name: "Copy <label>" (a `copyWhat…` value). */
  label: string;
  /**
   * 'code' is a token in monospace that wraps anywhere rather than widen the
   * page; 'text' reads as prose; 'file' is a multi-line config snippet shown
   * exactly as written, under a small header; 'prompt' has a collapsible preview.
   */
  kind?: 'code' | 'text' | 'file' | 'prompt';
  /** A 'file' snippet's header: what kind of file it belongs in. */
  caption?: string;
}) {
  const ns = useNamespaceCopy(connectionsCopy);
  const [state, setState] = useState<'idle' | 'copied' | 'manual'>('idle');
  const [expanded, setExpanded] = useState(false);
  const previewId = useId();
  const code = useRef<HTMLElement>(null);
  const previewShown = expanded || state === 'manual';
  useEffect(() => {
    if (state === 'manual') selectContents(code.current);
  }, [state]);
  useEffect(() => {
    if (state !== 'copied') return;
    const timer = window.setTimeout(() => setState('idle'), 2500);
    return () => window.clearTimeout(timer);
  }, [state]);
  const copy = () =>
    void Promise.resolve()
      .then(() => navigator.clipboard.writeText(value))
      .then(
        () => setState('copied'),
        () => {
          if (state === 'manual') selectContents(code.current);
          setState('manual');
        },
      );
  const copied = state === 'copied';
  const named = formatCopy(copied ? ns.copiedNamed : ns.copyNamed, { what: label });
  // A visible label is one text run ("Copy setup prompt"), so the button's gap
  // sits only after the icon. Elsewhere the button shows the verb and its name
  // is the whole phrase, whose word order each language sets.
  const button = (
    <button
      type="button"
      className="ws-agent-copy"
      data-copied={copied}
      aria-label={kind === 'prompt' ? undefined : named}
      onClick={copy}
    >
      <Glyph name={copied ? 'check' : 'copy'} size={13} />
      {kind === 'prompt' ? <span>{named}</span> : copied ? ns.copiedAction : ns.copyAction}
    </button>
  );
  return (
    <div className="ws-agent-copy-line" data-kind={kind}>
      {kind === 'prompt' ? (
        <>
          <div className="ws-agent-prompt-actions">
            {button}
            <button
              type="button"
              className="ws-agent-disclosure"
              aria-expanded={previewShown}
              aria-controls={previewShown ? previewId : undefined}
              onClick={() => {
                setExpanded(!previewShown);
                if (state === 'manual') setState('idle');
              }}
            >
              {previewShown ? ns.copyPromptHide : ns.copyPromptShow}
            </button>
          </div>
          {previewShown && (
            <code id={previewId} ref={code}>
              {value}
            </code>
          )}
        </>
      ) : kind === 'file' ? (
        <>
          <div className="ws-agent-copy-head">
            <span>{caption}</span>
            {button}
          </div>
          <code ref={code}>{value}</code>
        </>
      ) : (
        <>
          <code ref={code}>{value}</code>
          {button}
        </>
      )}
      <span className="ws-sr-only" role="status">
        {copied ? formatCopy(ns.copiedStatus, { what: label }) : ''}
      </span>
      {state === 'manual' && (
        <p className="ws-agent-copy-hint" role="status">
          {ns.copyManualHint}
        </p>
      )}
    </div>
  );
}
